import React, { Fragment } from 'react';
import { withTheme } from 'theming';
import { get } from 'lodash';

import { Div, P, Span, Img } from 'MoshtixShared/component-element';
import { Button } from 'MoshtixShared/component-button';
import { Icon } from 'MoshtixShared/component-icon';
import { ThemeInterface } from 'Types/theme';

import { EventInterface, TicketInterface } from '../../../../types/app';
import { checkPurchaserViewingAssignedTicket } from '../../../../utils/ticketHelper';
import { TicketActions } from '../ticket-actions';
import styles from './styles';
import assignIcon from '../../../../pages/event/component-assign-ticket-dialog/Assign-icon.png';

interface TicketDetailsProps {
  ticket: TicketInterface;
  event: EventInterface;
  theme: ThemeInterface;
  toggleTicketDetails: () => void;
  onCloseClick: () => void;
  isViewingAsAssignee: boolean;
}

const ASSIGNED_TICKET_TEXT = 'This ticket has been assigned to event attendee. To revoke, go to manage ticket.';

const TicketDetailsWithoutTheme: React.SFC<TicketDetailsProps> = ({
  event,
  ticket,
  order,
  theme,
  toggleTicketDetails,
  onCloseClick,
  isViewingAsAssignee,
}: TicketDetailsProps) => {
  // Purchaser is viewing a ticket they assigned to someone else
  const isPurchaserViewingAssignedTicket = checkPurchaserViewingAssignedTicket(ticket, isViewingAsAssignee);

  return (
    <Div css={styles.ticketDetailsContainer({ theme })}>
      <Div css={styles.ticketDetailsMobileControlContainer}>
        <Button css={styles.ticketDetailsBackToTicket} onClick={toggleTicketDetails}>
          Back to Ticket
          <Icon type="back" color={theme.palette.black} />
        </Button>
        <Button css={styles.ticketDetailsBackToEvent} onClick={onCloseClick}>
          Back to Event
          <Icon type="close" color={theme.palette.black} />
        </Button>
      </Div>
      {isPurchaserViewingAssignedTicket && (
        <Div css={styles.assignedTicketMessage({ theme })}>
          <Img src={assignIcon} css={styles.assignedTicketIcon} />
          <P css={styles.assignedTicketText}>{ASSIGNED_TICKET_TEXT}</P>
        </Div>
      )}
      <Div css={styles.ticketActionsDetails}>
        <TicketActions event={event} ticket={ticket} order={order} isViewingAsAssignee={isViewingAsAssignee} />
      </Div>
      <Div css={styles.ticketDetailsBackground({ theme })}>
        <P css={styles.ticketDetailsLabelCss}>Event</P>
        <P css={styles.ticketDetailsCss}>{event.name}</P>
        <P css={styles.ticketDetailsLabelCss}>Ticket type</P>
        <P css={styles.ticketDetailsCss}>
          {ticket.collectorTicket ? ticket.ticketTypeName : get(ticket, 'ticketType.name') || 'Unknown'}
        </P>
        {!get(ticket, 'ticketType.suppressDistribution') &&
        !get(ticket, 'ticketType.collectorTicketCollectableOnly') && (
          <>
            <P css={styles.ticketDetailsLabelCss}>Ticket no.</P>
            <P css={styles.ticketDetailsCss}>{ticket.barcode}</P>
          </>
        )}
        {order && (
          <>
            <P css={styles.ticketDetailsLabelCss}>Customer no.</P>
            <P css={styles.ticketDetailsCss}>
              {order && order.customer && order.customer.id ? order.customer.id : 'N/A'}
            </P>
            <P css={styles.ticketDetailsLabelCss}>Purchase Date</P>
            <P css={styles.ticketDetailsCss}>{order && order.createdDate ? order.createdDate : 'N/A'}</P>
          </>
        )}
        <P css={styles.ticketDetailsLabelCss}>Price</P>
        <P css={styles.ticketDetailsCss}>
          {ticket.discount ? (
            <Fragment>
              <Span css={styles.strikethrough}>{`$${ticket.totalCost}`}</Span>
              <Span>{`$${(ticket.totalCost - ticket.discount).toFixed(2)}`}</Span>
            </Fragment>
          ) : (
            `$${ticket.totalCost}`
          )}
        </P>
      </Div>
    </Div>
  );
};

export const TicketDetails = withTheme(TicketDetailsWithoutTheme);
